import type { SupportedLocale } from '@/i18n/config';

const FR_PREFIX = '/fr';

/**
 * Returns the locale implied by a URL pathname.
 * `/fr`, `/fr/`, `/fr/anything` → 'fr-CA'. Everything else → 'en-CA'.
 */
export const localeFromPath = (pathname: string): SupportedLocale => {
  if (pathname === FR_PREFIX || pathname.startsWith(`${FR_PREFIX}/`)) {
    return 'fr-CA';
  }
  return 'en-CA';
};

/**
 * Strips the /fr prefix from a pathname. `/fr/about` → `/about`,
 * `/fr` → `/`, `/about` → `/about`. Used to pair a URL with its
 * equivalent in the other locale.
 */
export const stripLocalePrefix = (pathname: string): string => {
  if (pathname === FR_PREFIX) return '/';
  if (pathname.startsWith(`${FR_PREFIX}/`)) return pathname.slice(FR_PREFIX.length) || '/';
  return pathname;
};

/**
 * Given a locale and an already-localeless pathname, returns the URL
 * that serves that pathname in that locale.
 *   buildLocalizedPath('fr-CA', '/about') → '/fr/about'
 *   buildLocalizedPath('fr-CA', '/')       → '/fr'
 *   buildLocalizedPath('en-CA', '/about') → '/about'
 */
export const buildLocalizedPath = (
  locale: SupportedLocale,
  pathname: string,
): string => {
  const clean = stripLocalePrefix(pathname);
  if (locale === 'fr-CA') {
    return clean === '/' ? FR_PREFIX : `${FR_PREFIX}${clean}`;
  }
  return clean;
};

/**
 * Returns the same pathname in the *other* locale.
 *   toggleLocalePath('/about')    → '/fr/about'
 *   toggleLocalePath('/fr/about') → '/about'
 */
export const toggleLocalePath = (pathname: string): string => {
  const current = localeFromPath(pathname);
  const target: SupportedLocale = current === 'fr-CA' ? 'en-CA' : 'fr-CA';
  return buildLocalizedPath(target, pathname);
};

/**
 * Routes whose body is fully translated into French (the AI readiness quiz is English-only, so it is not listed). Only these are indexable in French:
 * their /fr URL is self-canonical, carries French metadata and an hreflang pair. Every other
 * /fr/* URL mirrors an English page and canonicalizes to it (see LocaleSync).
 */
export const FR_INDEXABLE_PATHS: readonly string[] = ['/about', '/contact', '/case-studies'];

const SITE_ORIGIN = 'https://www.brainfyai.com';

/** Canonical URL for a pathname, honouring the French-indexable policy above. */
export const canonicalUrlFor = (pathname: string): string => {
  const english = stripLocalePrefix(pathname);
  if (localeFromPath(pathname) === 'fr-CA' && FR_INDEXABLE_PATHS.includes(english)) {
    return `${SITE_ORIGIN}${buildLocalizedPath('fr-CA', english)}`;
  }
  return `${SITE_ORIGIN}${english}`;
};
